import React, { useMemo } from "react";
import { useComparison } from "../context/ComparisonContext";

const Comparison: React.FC = () => {
    const { firstText, setFirstText, secondText, setSecondText, loading, result, error, handleClear, handleSubmit } = useComparison();

    const text1Units = result?.result?.text1_units || [];
    const text2Units = result?.result?.text2_units || [];
    const alignmentPairs: [number, number][] = result?.result?.alignment_pairs || [];

    const { text1ColorMap, text2ColorMap } = useMemo(() => {
        // Union-Find: group sentences connected by alignment pairs into components
        const parent = new Map<string, string>();
        const find = (x: string): string => {
            if (!parent.has(x)) parent.set(x, x);
            if (parent.get(x) !== x) parent.set(x, find(parent.get(x)!));
            return parent.get(x)!;
        };
        const union = (a: string, b: string) => {
            const ra = find(a), rb = find(b);
            if (ra !== rb) parent.set(ra, rb);
        };

        for (const [i, j] of alignmentPairs) {
            union(`1-${i}`, `2-${j}`);
        }

        // Assign one color per connected component (unique root)
        const rootToColor = new Map<string, string>();
        let colorIdx = 0;
        const getColor = (root: string) => {
            if (!rootToColor.has(root)) {
                const hue = (colorIdx++ * 137.508) % 360;
                rootToColor.set(root, `hsl(${hue}, 65%, 75%)`);
            }
            return rootToColor.get(root)!;
        };

        const text1ColorMap = new Map<number, string>();
        const text2ColorMap = new Map<number, string>();
        for (const [i, j] of alignmentPairs) {
            const color = getColor(find(`1-${i}`));
            text1ColorMap.set(i, color);
            text2ColorMap.set(j, color);
        }

        return { text1ColorMap, text2ColorMap };
    }, [alignmentPairs]);

    return (
        <>
            <div className="max-w-4xl mx-auto mb-6 text-center text-gray-600 dark:text-slate-100">
                <p>
                    Paste two earnings reports to compare them. The analysis will highlight 
                    aligned content between the reports.
                </p>
            </div>
            <div className="w-full mx-auto p-6 bg-white dark:bg-slate-900 rounded-lg shadow-md">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mb-4">
                    Compare Your Earnings Data
                </h2>
                
                <div className="flex flex-col md:flex-row gap-6 w-full items-start">
                    <div className={"flex-1 w-full transition-opacity duration-300 opacity-100"}>
                        <label className="block text-sm font-semibold mb-2 dark:text-slate-300">
                            Paste First Earnings Report 
                        </label>
                        <textarea
                            value={firstText}
                            placeholder={"Paste your first earnings report here..."}
                            onChange={(e) => setFirstText(e.target.value)}
                            className="w-full h-80 p-3 dark:bg-slate-900 border border-gray-300 dark:border-slate-800 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:cursor-not-allowed shadow-sm"
                        />
                    </div>

                    <div className={"flex-1 w-full transition-opacity duration-300 opacity-100"}>
                        <label className="block text-sm font-semibold mb-2 dark:text-slate-300">
                            Paste Second Earnings Report
                        </label>
                        <textarea
                            value={secondText}
                            placeholder={"Paste your second earnings report here..."}
                            onChange={(e) => setSecondText(e.target.value)}
                            className="w-full h-80 p-3 dark:bg-slate-900 border border-gray-300 dark:border-slate-800 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:cursor-not-allowed shadow-sm"
                        />
                    </div>
                </div>

                <button
                    disabled={(!firstText.trim() && !secondText.trim()) || loading}
                    onClick={handleClear}
                    className="mt-4 w-full flex items-center justify-center bg-red-600 text-white font-bold py-2 px-4 rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                    Clear Input
                </button>

                <button
                    disabled={!firstText.trim() || !secondText.trim() || loading}
                    onClick={handleSubmit}
                    className="mt-2 w-full flex items-center justify-center bg-blue-600 text-white font-bold py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                    {loading && (
                    <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white dark:text-slate-300"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        ></circle>
                        <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                    </svg>
                    )}
                    {loading ? "Submitting..." : "Submit Input"}
                </button>

                {error && (
                    <div className="mt-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md">
                        <p>
                            <span className="font-bold">Error:</span> {error}
                        </p>
                    </div>
                )}

                {result && !loading && (
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                        {firstText.trim() && (
                            <div className="p-5 bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 rounded-xl shadow-sm">
                                <div className="text-xs font-bold text-blue-600 dark:text-blue-500 uppercase mb-3">
                                    First Earnings Report
                                </div>
                                <div className="text-sm text-gray-600 dark:text-slate-400 whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto">
                                {text1Units.map((sentence: string, idx: number) => (
                                    <span
                                        key={`t1-${idx}`}
                                        style={text1ColorMap.has(idx) ? { backgroundColor: text1ColorMap.get(idx), borderRadius: '2px' } : undefined}
                                    >
                                        {sentence}{" "}
                                    </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {secondText.trim() && (
                            <div className="p-5 bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 rounded-xl shadow-sm">
                                <div className="text-xs font-bold text-blue-600 dark:text-blue-500 uppercase mb-3">
                                    Second Earnings Report
                                </div>
                                <div className="text-sm text-gray-600 dark:text-slate-400 whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto">
                                    {text2Units.map((sentence: string, idx: number) => (
                                        <span
                                            key={`t2-${idx}`}
                                            style={text2ColorMap.has(idx) ? { backgroundColor: text2ColorMap.get(idx), borderRadius: '2px' } : undefined}
                                        >
                                            {sentence}{" "}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default Comparison;